/**
 * Component Renderer
 * Renders actual Bootstrap components for WYSIWYG editing in the Interface Builder
 */

import React, { useState } from 'react';

export interface ComponentData {
  id: string;
  name: string;
  type: string;
  icon: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  properties?: Record<string, any>;
}

interface ComponentRendererProps {
  component: ComponentData;
  isSelected?: boolean;
  isDragging?: boolean;
  onSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string, updates: Partial<ComponentData>) => void;
}

export const ComponentRenderer= ({
  component,
  isSelected = false,
  isDragging = false,
  onSelect,
  onDelete,
  onUpdate
}: ComponentRendererProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(component.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(component.id);
  };

  const renderComponent = () => {
    const props = component.properties || {};
    
    switch (component.type) {
      case 'Container':
        return (
          <div className="container-fluid border border-dashed border-primary p-3" style={{ minHeight: '100px' }}>
            <div className="text-muted text-center">
              <small>📦 Container - Drop components here</small>
            </div>
          </div>
        );
        
      case 'Row':
        return (
          <div className="row border border-dashed border-info p-2" style={{ minHeight: '60px' }}>
            <div className="text-muted text-center w-100">
              <small>➖ Bootstrap Row</small>
            </div>
          </div>
        );
        
      case 'Column':
        return (
          <div className="col border border-dashed border-success p-2" style={{ minHeight: '60px' }}>
            <div className="text-muted text-center">
              <small>▌ Column</small>
            </div>
          </div>
        );
        
      case 'Card':
        return (
          <div className="card shadow-sm" style={{ 
            minWidth: props.minWidth || '250px',
            minHeight: props.minHeight || '200px',
            width: props.width || 'auto',
            height: props.height || 'auto'
          }}>
            <div className="card-body">
              <h5 className="card-title">{props.title || 'Card Title'}</h5>
              <p className="card-text">{props.text || 'This is a sample card component. Click to edit its properties.'}</p>
              <button className="btn btn-primary">{props.buttonText || 'Action'}</button>
            </div>
          </div>
        );
        
      case 'Input':
        return (
          <div className="mb-3" style={{
            minWidth: props.minWidth || '200px',
            width: props.width || 'auto'
          }}>
            <label className="form-label">{props.label || 'Input Field'}</label>
            <input 
              type={props.type || 'text'} 
              className="form-control" 
              placeholder={props.placeholder || 'Enter text...'}
              value={props.value || ''}
              readOnly
            />
          </div>
        );
        
      case 'Button':
        return (
          <button 
            className={`btn ${props.variant || 'btn-primary'}`}
            disabled={props.disabled}
            style={{
              minWidth: props.minWidth || '120px',
              minHeight: props.minHeight || '40px',
              width: props.width || 'auto',
              height: props.height || 'auto'
            }}
          >
            {props.text || 'Button'}
          </button>
        );
        
      case 'Select':
        return (
          <div className="mb-3">
            <label className="form-label">{props.label || 'Select Option'}</label>
            <select className="form-select">
              <option>{props.placeholder || 'Choose...'}</option>
              <option>Option 1</option>
              <option>Option 2</option>
              <option>Option 3</option>
            </select>
          </div>
        );
        
      case 'Checkbox':
        return (
          <div className="form-check">
            <input className="form-check-input" type="checkbox" />
            <label className="form-check-label">
              {props.label || 'Checkbox Label'}
            </label>
          </div>
        );
        
      case 'Radio':
        return (
          <div className="form-check">
            <input className="form-check-input" type="radio" name={`radio-${component.id}`} />
            <label className="form-check-label">
              {props.label || 'Radio Option'}
            </label>
          </div>
        );
        
      case 'Textarea':
        return (
          <div className="mb-3">
            <label className="form-label">{props.label || 'Text Area'}</label>
            <textarea 
              className="form-control" 
              rows={props.rows || 3}
              placeholder={props.placeholder || 'Enter multi-line text...'}
              readOnly
            />
          </div>
        );
        
      case 'Table':
        return (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Header 1</th>
                  <th>Header 2</th>
                  <th>Header 3</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Data 1</td>
                  <td>Data 2</td>
                  <td>Data 3</td>
                </tr>
                <tr>
                  <td>Sample</td>
                  <td>Content</td>
                  <td>Here</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
        
      case 'List':
        return (
          <ul className="list-group">
            <li className="list-group-item">List Item 1</li>
            <li className="list-group-item">List Item 2</li>
            <li className="list-group-item">List Item 3</li>
          </ul>
        );
        
      case 'Badge':
        return (
          <span className={`badge ${props.variant || 'bg-primary'}`}>
            {props.text || 'Badge'}
          </span>
        );
        
      case 'Progress':
        return (
          <div>
            <label className="form-label">{props.label || 'Progress'}</label>
            <div className="progress">
              <div 
                className="progress-bar" 
                style={{ width: `${props.value || 60}%` }}
              >
                {props.showValue !== false && `${props.value || 60}%`}
              </div>
            </div>
          </div>
        );
        
      case 'Navbar':
        return (
          <nav className="navbar navbar-expand-lg navbar-light bg-light">
            <div className="container-fluid">
              <a className="navbar-brand" href="#">{props.brand || 'Brand'}</a>
              <div className="navbar-nav">
                <a className="nav-link active" href="#">Home</a>
                <a className="nav-link" href="#">About</a>
                <a className="nav-link" href="#">Contact</a>
              </div>
            </div>
          </nav>
        );
        
      case 'Breadcrumb':
        return (
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="#">Home</a></li>
              <li className="breadcrumb-item"><a href="#">Library</a></li>
              <li className="breadcrumb-item active" aria-current="page">Current</li>
            </ol>
          </nav>
        );
        
      case 'Pagination':
        return (
          <nav>
            <ul className="pagination">
              <li className="page-item"><a className="page-link" href="#">Previous</a></li>
              <li className="page-item active"><a className="page-link" href="#">1</a></li>
              <li className="page-item"><a className="page-link" href="#">2</a></li>
              <li className="page-item"><a className="page-link" href="#">3</a></li>
              <li className="page-item"><a className="page-link" href="#">Next</a></li>
            </ul>
          </nav>
        );
        
      case 'Tabs':
        return (
          <div>
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <a className="nav-link active" href="#">Tab 1</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#">Tab 2</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#">Tab 3</a>
              </li>
            </ul>
            <div className="tab-content p-3 border border-top-0">
              <div className="tab-pane active">
                <p>Content for Tab 1. Click tabs to switch between different content sections.</p>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="p-3 border border-dashed border-secondary rounded">
            <div className="text-muted text-center">
              <span className="me-2">{component.icon}</span>
              <small>Unknown component: {component.name}</small>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="position-relative">
      {/* Component Content */}
      {renderComponent()}
    </div>
  );
};